import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PROJECT_ROLES,
  canGrantProjectRole,
  canProject,
  type ProjectMember,
  type ProjectRole,
} from '@allemnii-pulse/shared';
import { api, ApiError } from '../../lib/api.js';
import { useCollection } from '../../lib/firestore.js';
import { useUserProfiles } from '../../lib/userProfiles.js';
import { UserStatusDot } from '../../components/UserStatus.js';
import { useProject } from './ProjectProvider.js';
import { ProjectTrainingInstructionsCard } from '../training/TrainingInstructionsCard.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Checkbox } from '../../components/ui/Checkbox.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select.js';
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/Dialog.js';

const ROLE_LABEL: Record<ProjectRole, string> = {
  owner: 'Owner',
  lead: 'Lead',
  member: 'Member',
  viewer: 'Viewer',
};

function roleBadgeVariant(role: ProjectRole) {
  if (role === 'owner') return 'default';
  if (role === 'lead') return 'secondary';
  return 'outline';
}

function errorMessage(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

/** Project settings. What each control offers follows the RBAC matrix in
 * packages/shared/src/permissions.ts: leads manage members and viewers, rename the project and
 * toggle viewer comments; only an owner changes the lead, grants lead/owner, archives or deletes. */
export function ProjectSettingsPage() {
  const { projectId, project, role } = useProject();
  const navigate = useNavigate();
  const canManageMembers = canProject(role, 'project.members.manage');
  const canUpdate = canProject(role, 'project.update');
  const canChangeLead = canProject(role, 'project.leads.manage');
  const canArchive = canProject(role, 'project.archive');
  const canDelete = canProject(role, 'project.delete');

  const { data: members, loading: membersLoading } = useCollection<ProjectMember>(
    `projects/${projectId}/members`,
  );
  const profiles = useUserProfiles(members.map((m) => m.uid));
  const nameOf = (uid: string) => profiles[uid]?.displayName ?? profiles[uid]?.email ?? uid;

  /** The roles this caller may grant to someone currently holding `from` (null = new member). */
  const grantableRoles = (from: ProjectRole | null) =>
    PROJECT_ROLES.filter((r) => canGrantProjectRole(role, from, r));
  const addableRoles = grantableRoles(null);

  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<ProjectRole>('member');
  const [addingMember, setAddingMember] = useState(false);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [busyUid, setBusyUid] = useState<string | null>(null);

  async function handleAddMember(e: React.FormEvent) {
    e.preventDefault();
    if (!memberEmail.trim()) return;
    setAddingMember(true);
    setMemberError(null);
    try {
      await api.post(`/projects/${projectId}/members`, {
        email: memberEmail.trim(),
        role: memberRole,
      });
      setMemberEmail('');
    } catch (err) {
      setMemberError(errorMessage(err, 'Could not add that member. Please try again.'));
    } finally {
      setAddingMember(false);
    }
  }

  async function handleChangeRole(uid: string, newRole: ProjectRole) {
    setBusyUid(uid);
    setMemberError(null);
    try {
      await api.patch(`/projects/${projectId}/members/${uid}`, { role: newRole });
    } catch (err) {
      setMemberError(errorMessage(err, 'Could not change that role. Please try again.'));
    } finally {
      setBusyUid(null);
    }
  }

  async function handleRemoveMember(uid: string) {
    setBusyUid(uid);
    setMemberError(null);
    try {
      await api.delete(`/projects/${projectId}/members/${uid}`);
    } catch (err) {
      setMemberError(errorMessage(err, 'Could not remove that member. Please try again.'));
    } finally {
      setBusyUid(null);
    }
  }

  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [settingError, setSettingError] = useState<string | null>(null);
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (project) setName(project.name);
  }, [project]);

  const isArchived = project?.status === 'archived';
  const nameChanged = project && name.trim() !== project.name && name.trim().length > 0;

  async function handleSaveName() {
    if (!nameChanged) return;
    setSavingName(true);
    setNameError(null);
    try {
      await api.patch(`/projects/${projectId}`, { name: name.trim() });
    } catch {
      setNameError('Could not rename the project. Please try again.');
    } finally {
      setSavingName(false);
    }
  }

  async function handlePatchSetting(patch: { leadUid?: string; viewerCanComment?: boolean }) {
    setSettingError(null);
    try {
      await api.patch(`/projects/${projectId}`, patch);
    } catch (err) {
      setSettingError(errorMessage(err, 'Could not save that setting. Please try again.'));
    }
  }

  async function handleToggleArchive() {
    setArchiving(true);
    setArchiveError(null);
    try {
      await api.patch(`/projects/${projectId}`, { status: isArchived ? 'active' : 'archived' });
      setConfirmArchiveOpen(false);
    } catch {
      setArchiveError('Could not update the project status. Please try again.');
    } finally {
      setArchiving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/projects/${projectId}`);
      navigate('/projects');
    } catch (err) {
      setDeleteError(errorMessage(err, 'Could not delete the project. Please try again.'));
      setDeleting(false);
    }
  }

  if (!project) {
    return <div className="p-4 text-sm text-muted sm:p-6">Loading…</div>;
  }

  // Anyone below lead can be the new lead; the API promotes them. Viewers are left out — making
  // a read-only member the lead in one step is more likely a slip than intent.
  const leadCandidates = members.filter((m) => m.role !== 'viewer');

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 p-4 sm:p-6">
      <div>
        <h1 className="text-lg font-semibold">Settings</h1>
        <p className="text-sm text-muted">Manage this project's name, members and lifecycle.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            Everyone with access to this project. Adding someone requires them to already be a
            member of the org. Org owners and admins also have access without being listed.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {membersLoading && <p className="text-sm text-muted">Loading…</p>}
          {!membersLoading && members.length === 0 && (
            <p className="text-sm text-muted">No members yet.</p>
          )}
          {members.map((m) => {
            const profile = profiles[m.uid];
            const isLead = m.uid === project.leadUid;
            const roleOptions = grantableRoles(m.role);
            const canChange = roleOptions.some((r) => r !== m.role) && !isLead;
            const canRemove = canGrantProjectRole(role, m.role, null) && !isLead;
            return (
              // Stacked on a phone: the role select and Remove button beside a name and email
              // would leave the name only a few characters wide.
              <div
                key={m.uid}
                className="flex flex-col gap-2 py-1 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="flex flex-wrap items-center gap-x-1.5 text-sm wrap-anywhere">
                    <UserStatusDot uid={m.uid} />
                    {profile?.displayName ?? profile?.email ?? m.uid}
                    {isLead && <span className="text-xs text-muted">· project lead</span>}
                  </span>
                  {profile?.displayName && profile?.email && (
                    <span className="pl-3.5 text-xs text-muted wrap-anywhere">{profile.email}</span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2 pl-3.5 sm:pl-0">
                  {canChange ? (
                    <Select
                      value={m.role}
                      disabled={busyUid === m.uid}
                      onValueChange={(v) => void handleChangeRole(m.uid, v as ProjectRole)}
                    >
                      <SelectTrigger className="h-10 w-28 text-xs sm:h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(new Set([m.role, ...roleOptions])).map((r) => (
                          <SelectItem key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant={roleBadgeVariant(m.role)}>{m.role}</Badge>
                  )}
                  {canRemove && (
                    <Button
                      type="button"
                      variant="destructive"
                      className="h-10 sm:h-8"
                      disabled={busyUid === m.uid}
                      onClick={() => void handleRemoveMember(m.uid)}
                    >
                      {busyUid === m.uid ? 'Working…' : 'Remove'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          {memberError && <p className="text-sm text-danger">{memberError}</p>}
        </CardContent>
        {canManageMembers && addableRoles.length > 0 && (
          <form onSubmit={(e) => void handleAddMember(e)}>
            <CardContent className="flex flex-col gap-3 border-t border-border pt-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="member-email">Add a member by email</Label>
                <p className="text-xs text-muted">
                  They must already have signed in to the app and be a member of this org. Org
                  guests can be at most a member.
                </p>
                <Input
                  id="member-email"
                  type="email"
                  value={memberEmail}
                  onChange={(e) => setMemberEmail(e.target.value)}
                  placeholder="teammate@example.com"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="member-role">Role</Label>
                <Select value={memberRole} onValueChange={(v) => setMemberRole(v as ProjectRole)}>
                  <SelectTrigger id="member-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...addableRoles].reverse().map((r) => (
                      <SelectItem key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
            <CardFooter className="justify-end">
              <Button
                type="submit"
                variant="default"
                disabled={addingMember || !memberEmail.trim()}
              >
                {addingMember ? 'Adding…' : 'Add member'}
              </Button>
            </CardFooter>
          </form>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Project lead</CardTitle>
          <CardDescription>
            Gets blocker and ready-for-testing notifications. Only a project owner can change the
            lead; the new lead is promoted to the lead role if needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-1.5">
          {canChangeLead ? (
            <Select
              value={project.leadUid}
              onValueChange={(v) => void handlePatchSetting({ leadUid: v })}
            >
              <SelectTrigger aria-label="Project lead">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {leadCandidates.map((m) => (
                  <SelectItem key={m.uid} value={m.uid}>
                    {nameOf(m.uid)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-sm">{nameOf(project.leadUid)}</span>
          )}
        </CardContent>
      </Card>

      {project.projectType === 'training' && (
        <ProjectTrainingInstructionsCard
          projectId={projectId}
          canManage={canProject(role, 'training.manage')}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Viewer comments</CardTitle>
          <CardDescription>
            Viewers can always read the project. Turn this on to let them comment on tasks too —
            useful for client stakeholders. They still can't edit anything or see the activity feed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={project.viewerCanComment === true}
              disabled={!canUpdate}
              onCheckedChange={(checked) =>
                void handlePatchSetting({ viewerCanComment: checked === true })
              }
            />
            Viewers can comment
          </label>
          {settingError && <p className="mt-2 text-sm text-danger">{settingError}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Project name</CardTitle>
          <CardDescription>
            Shown across the app, including project lists and breadcrumbs.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-1.5">
          <Label htmlFor="settings-project-name">Name</Label>
          <Input
            id="settings-project-name"
            value={name}
            disabled={!canUpdate}
            onChange={(e) => setName(e.target.value)}
          />
          {nameError && <p className="text-sm text-danger">{nameError}</p>}
        </CardContent>
        {canUpdate && (
          <CardFooter className="justify-end">
            <Button
              variant="default"
              disabled={!nameChanged || savingName}
              onClick={() => void handleSaveName()}
            >
              {savingName ? 'Saving…' : 'Save'}
            </Button>
          </CardFooter>
        )}
      </Card>

      <Card className="border-danger/40">
        <CardHeader>
          <CardTitle>{isArchived ? 'Restore project' : 'Archive project'}</CardTitle>
          <CardDescription>
            {isArchived
              ? 'This project is archived. Restoring it makes it active again.'
              : 'Archiving hides this project from active views without deleting any data. Only a project owner can archive.'}
          </CardDescription>
        </CardHeader>
        {archiveError && (
          <CardContent>
            <p className="text-sm text-danger">{archiveError}</p>
          </CardContent>
        )}
        {canArchive && (
          <CardFooter className="justify-end">
            <Button variant="destructive" onClick={() => setConfirmArchiveOpen(true)}>
              {isArchived ? 'Restore project' : 'Archive project'}
            </Button>
          </CardFooter>
        )}
      </Card>

      {canDelete && isArchived && (
        <Card className="border-danger/40">
          <CardHeader>
            <CardTitle>Delete project</CardTitle>
            <CardDescription>
              Permanently deletes this project with its documents, tasks, deliverables and activity.
              The audit log keeps a record that it existed. This can't be undone.
            </CardDescription>
          </CardHeader>
          {deleteError && (
            <CardContent>
              <p className="text-sm text-danger">{deleteError}</p>
            </CardContent>
          )}
          <CardFooter className="justify-end">
            <Button variant="destructive" onClick={() => setConfirmDeleteOpen(true)}>
              Delete project
            </Button>
          </CardFooter>
        </Card>
      )}

      <Dialog open={confirmArchiveOpen} onOpenChange={setConfirmArchiveOpen}>
        <DialogHeader>
          <DialogTitle>
            {isArchived ? 'Restore this project?' : 'Archive this project?'}
          </DialogTitle>
          <DialogDescription>
            {isArchived
              ? `"${project.name}" will become active again and reappear in active project views.`
              : `"${project.name}" will be hidden from active project views. You can restore it later from this page.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-wrap">
          <Button type="button" onClick={() => setConfirmArchiveOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={archiving}
            onClick={() => void handleToggleArchive()}
          >
            {archiving ? 'Working…' : isArchived ? 'Restore' : 'Archive'}
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogHeader>
          <DialogTitle>Delete this project permanently?</DialogTitle>
          <DialogDescription>
            {`"${project.name}" and everything in it will be deleted. This can't be undone.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-wrap">
          <Button type="button" onClick={() => setConfirmDeleteOpen(false)}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={deleting} onClick={() => void handleDelete()}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
